import React, { useState, useEffect, useRef } from 'react';
import { getInitialState, saveState, autoBalanceTeams } from './services/store';
import {
  AppState,
  Match,
  MatchRSVP,
  GroupMemberProfile,
  UserAccount,
  GroupSettings,
  Venue,
  PaymentRecord,
  MatchComment,
  Announcement,
  Poll,
  ActivityLog,
  RSVPStatus,
  CardEditMode,
  PlayerMatchStat,
  MatchJoinRequest
} from './types';
import { Navbar } from './components/Navbar';
import { MobileNav } from './components/MobileNav';
import { NextMatchHeroCard } from './components/NextMatchHeroCard';
import { CreateMatchModal } from './components/CreateMatchModal';
import { MatchDetailsModal } from './components/MatchDetailsModal';
import { ManagePlayerModal } from './components/ManagePlayerModal';
import { ProfileView } from './components/ProfileView';
import { AdminSettingsView } from './components/AdminSettingsView';
import { TurfDirectoryView } from './components/TurfDirectoryView';
import { PaymentsView } from './components/PaymentsView';
import { LeaderboardView } from './components/LeaderboardView';
import { CommunityView } from './components/CommunityView';
import { WorldFootballView } from './components/WorldFootballView';
import { ActivityLogView } from './components/ActivityLogView';
import { MediaLibraryView } from './components/MediaLibraryView';
import { RegisterPlayerModal } from './components/RegisterPlayerModal';
import { AuthLoginModal } from './components/AuthLoginModal';
import { PlayerProfileModal } from './components/PlayerProfileModal';
import { PlayerComparisonModal } from './components/PlayerComparisonModal';
import { PostMatchReportModal } from './components/PostMatchReportModal';
import { PwaInstallBanner } from './components/PwaInstallBanner';
import { JoinMatchRequestModal } from './components/JoinMatchRequestModal';
import { ApkDownloadModal } from './components/ApkDownloadModal';

import { Calendar, Plus, Users, MapPin, DollarSign, Trophy, Sparkles, Share2, Activity, CheckCircle2, MessageSquare, ShieldAlert, Image as ImageIcon, PhoneCall, MessageCircle, UserPlus } from 'lucide-react';
import { formatTimeTo12Hour } from './utils/timeUtils';

const MATCH_LIFECYCLE_REFRESH_MS = 60 * 1000;

function getMatchStartDate(match: Match) {
  return new Date(`${match.date}T${match.startTime}:00`);
}

function getMatchEndDate(match: Match) {
  return new Date(`${match.date}T${match.endTime}:00`);
}

function sortMatchesByKickoff(matches: Match[], direction: 'asc' | 'desc' = 'asc') {
  const sorted = [...matches].sort((a, b) => getMatchStartDate(a).getTime() - getMatchStartDate(b).getTime());
  return direction === 'asc' ? sorted : sorted.reverse();
}

function deriveMatchStatus(match: Match, now: Date = new Date()): Match['status'] {
  if (match.isCancelled || match.status === 'cancelled') return 'cancelled';
  if (match.result?.statsRecorded) return 'completed';

  const start = getMatchStartDate(match);
  const end = getMatchEndDate(match);

  if (now >= end) return 'awaiting_report';
  if (now >= start) return 'live';
  return 'upcoming';
}

function applyMatchLifecycle(state: AppState, now: Date = new Date()): AppState {
  let changed = false;
  const lifecycleLogs: ActivityLog[] = [];

  const matches = state.matches.map((match) => {
    const nextStatus = deriveMatchStatus(match, now);
    if (nextStatus === match.status) return match;
    changed = true;

    if (nextStatus === 'awaiting_report' && match.status !== 'awaiting_report') {
      lifecycleLogs.push({
        id: `act_lifecycle_${match.id}_${now.getTime()}`,
        groupId: state.group.id,
        matchId: match.id,
        actorName: 'System',
        action: 'Match Awaiting Report',
        details: `${match.title} ended and is waiting for score + player stats.`,
        timestamp: 'Just now',
      });
    }

    return {
      ...match,
      status: nextStatus,
    };
  });

  if (!changed) return state;

  return {
    ...state,
    matches,
    activityLogs: lifecycleLogs.length ? [...lifecycleLogs, ...state.activityLogs] : state.activityLogs,
  };
}

function getVisibilityLabel(match: Pick<Match, 'visibility'>) {
  switch (match.visibility) {
    case 'public':
      return 'Public';
    case 'community':
      return 'Community';
    default:
      return 'Private';
  }
}

function getJoinPolicyLabel(match: Pick<Match, 'joinPolicy'>) {
  switch (match.joinPolicy) {
    case 'open_join':
      return 'Open Join';
    case 'approval_required':
      return 'Approval Required';
    default:
      return 'Members Only';
  }
}

export default function App() {
  const [state, setState] = useState<AppState>(getInitialState);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Persist nothing until the real server state has loaded. The initial state
  // is the demo/seed copy; saving it (e.g. via the lifecycle timer) would
  // overwrite the shared roster, ratings, stats and matches on the server.
  const hydratedRef = useRef(false);

  useEffect(() => {
    Promise.all([fetch('/api/auth/me'), fetch('/api/state')])
      .then(async ([authResponse, stateResponse]) => {
        if (!authResponse.ok) return;
        setIsAuthenticated(true);
        if (stateResponse.ok) {
          setState(applyMatchLifecycle(await stateResponse.json()));
          hydratedRef.current = true;
        }
      })
      .catch((error) => console.warn('Failed to restore authenticated session:', error))
      .finally(() => setAuthLoading(false));
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const payload = await response.json();
      if (!response.ok) return payload.error || 'Unable to sign in';
      setIsAuthenticated(true);
      const stateResponse = await fetch('/api/state');
      if (stateResponse.ok) {
        setState(applyMatchLifecycle(await stateResponse.json()));
        hydratedRef.current = true;
      }
      return null;
    } catch {
      return 'The server is unavailable. Please try again.';
    }
  };

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setIsAuthenticated(false);
  };
  const updateState = (updater: (prev: AppState) => AppState) => {
    setState((prev) => {
      const next = updater(prev);
      if (hydratedRef.current) saveState(next);
      return next;
    });
  };

  useEffect(() => {
    const syncLifecycle = () => {
      setState((prev) => {
        const next = applyMatchLifecycle(prev);
        if (next === prev) return prev;
        // Only persist lifecycle transitions once we are showing real server
        // state. Before hydration this would write the demo seed back to the
        // server and wipe the shared board.
        if (hydratedRef.current) saveState(next);
        return next;
      });
    };

    syncLifecycle();
    const interval = window.setInterval(syncLifecycle, MATCH_LIFECYCLE_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, []);

  // View & Modal Navigation
  const [currentTab, setCurrentTab] = useState<string>('home');
  const [showCreateMatch, setShowCreateMatch] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState<string | null>(null);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [managingPlayerId, setManagingPlayerId] = useState<string | null>(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [adminMode, setAdminMode] = useState<'full' | 'requests'>('full');
  const [showApkModal, setShowApkModal] = useState(false);
  const [shareTextModal, setShareTextModal] = useState<string | null>(null);
  const [copiedShare, setCopiedShare] = useState(false);

  // Player Card & Profile system states
  const [selectedPlayerProfileMember, setSelectedPlayerProfileMember] = useState<GroupMemberProfile | null>(null);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [comparePlayerAId, setComparePlayerAId] = useState<string | undefined>(undefined);
  const [comparePlayerBId, setComparePlayerBId] = useState<string | undefined>(undefined);
  const [postMatchReportMatch, setPostMatchReportMatch] = useState<Match | null>(null);
  const [joinRequestMatch, setJoinRequestMatch] = useState<Match | null>(null);
  const [matchDiscoveryQuery, setMatchDiscoveryQuery] = useState('');
  const [matchDiscoveryVisibility, setMatchDiscoveryVisibility] = useState<'all' | 'public' | 'community' | 'private'>('all');
  const [matchDiscoveryJoinPolicy, setMatchDiscoveryJoinPolicy] = useState<'all' | 'open_join' | 'approval_required' | 'members_only'>('all');
  const [matchDiscoveryFormat, setMatchDiscoveryFormat] = useState<'all' | Match['matchType']>('all');
  const [matchDiscoveryPriceBand, setMatchDiscoveryPriceBand] = useState<'all' | 'budget' | 'standard' | 'premium'>('all');

  // Quick lookup maps
  const membersMap = new Map<string, GroupMemberProfile>(state.members.map(m => [m.id, m]));
  const activeMember = state.members.find(m => m.userId === state.activeUserId) || state.members[0];
  const isOrganizer = ['owner', 'admin', 'organizer'].includes(activeMember?.role || '');
  const isAdminUser = ['owner', 'admin'].includes(activeMember?.role || '');
  const pendingApprovalCount = state.members.filter(m => m.verificationStatus === 'pending').length;
  const pendingJoinRequests = (state.joinRequests || []).filter((request) => request.status === 'pending');

  const handleSubmitCommunityRating = (targetMemberId: string, ratingData: any) => {
    updateState((prev) => {
      const nextMembers = prev.members.map((m) => {
        if (m.id !== targetMemberId) return m;
        const existingRatings = m.communityRatings || [];
        const authorId = activeMember.id;
        const filtered = existingRatings.filter((r) => r.authorMemberId !== authorId);
        const newRating = {
          id: `cr_${Date.now()}`,
          authorMemberId: authorId,
          targetMemberId,
          timestamp: new Date().toISOString(),
          ...ratingData,
        };
        return {
          ...m,
          communityRatings: [...filtered, newRating],
        };
      });
      return { ...prev, members: nextMembers };
    });
  };

  const handleApplyCommunitySuggestion = (targetMemberId: string, suggestedAttrs: any) => {
    updateState((prev) => {
      const nextMembers = prev.members.map((m) => {
        if (m.id !== targetMemberId) return m;
        return {
          ...m,
          footballCard: {
            ...m.footballCard,
            ...suggestedAttrs,
          },
        };
      });
      return { ...prev, members: nextMembers };
    });
  };

  const handleUpdateCosmeticTheme = (memberId: string, theme: any) => {
    updateState((prev) => {
      const nextMembers = prev.members.map((m) => {
        if (m.id !== memberId) return m;
        return {
          ...m,
          cardCosmetic: theme,
        };
      });
      return { ...prev, members: nextMembers };
    });
  };

  const handlePublishPostMatchReport = (
    matchId: string,
    resultData: {
      teamAScore: number;
      teamBScore: number;
      playerStats: PlayerMatchStat[];
      motmWinnerMemberId?: string;
    }
  ) => {
    updateState((prev) => {
      const targetMatch = prev.matches.find((m) => m.id === matchId);

      const nextMatches = prev.matches.map((m) => {
        if (m.id !== matchId) return m;
        return {
          ...m,
          status: 'completed' as const,
          motmWinnerMemberId: resultData.motmWinnerMemberId,
          result: {
            teamAScore: resultData.teamAScore,
            teamBScore: resultData.teamBScore,
            scorers: [],
            statsRecorded: true,
            playerStats: resultData.playerStats,
            notes: 'Official post-match report published',
            completedAt: new Date().toISOString(),
          },
        };
      });

      const statsByMember: { [id: string]: PlayerMatchStat } = {};
      resultData.playerStats.forEach((ps) => {
        statsByMember[ps.memberProfileId] = ps;
      });

      const nextMembers = prev.members.map((m) => {
        const pStat = statsByMember[m.id];
        if (!pStat) return m;

        const isTeamA = targetMatch?.teamA?.players.some((p) => p.memberProfileId === m.id);
        const isTeamB = targetMatch?.teamB?.players.some((p) => p.memberProfileId === m.id);

        let isWinner = false;
        let isLoss = false;
        let isDraw = false;

        if (resultData.teamAScore > resultData.teamBScore) {
          isWinner = Boolean(isTeamA);
          isLoss = Boolean(isTeamB);
        } else if (resultData.teamBScore > resultData.teamAScore) {
          isWinner = Boolean(isTeamB);
          isLoss = Boolean(isTeamA);
        } else {
          isDraw = true;
        }

        const isMotm = resultData.motmWinnerMemberId === m.id;

        const newMatchesPlayed = (m.stats.matchesPlayed || 0) + 1;
        const newGoals = (m.stats.goals || 0) + (pStat.goals || 0);
        const newAssists = (m.stats.assists || 0) + (pStat.assists || 0);
        const newWins = (m.stats.wins || 0) + (isWinner ? 1 : 0);
        const newLosses = (m.stats.losses || 0) + (isLoss ? 1 : 0);
        const newDraws = (m.stats.draws || 0) + (isDraw ? 1 : 0);
        const newMotm = (m.stats.motmCount || 0) + (isMotm ? 1 : 0);
        const newYellows = (m.stats.yellowCards || 0) + (pStat.yellowCards || 0);
        const newReds = (m.stats.redCards || 0) + (pStat.redCards || 0);
        const newHatTricks = (m.stats.hatTricks || 0) + (pStat.goals >= 3 ? 1 : 0);

        const isGk = m.primaryPosition === 'GK';
        const isCleanSheet = isGk && ((isTeamA && resultData.teamBScore === 0) || (isTeamB && resultData.teamAScore === 0));
        const newCleanSheets = (m.stats.cleanSheets || 0) + (isCleanSheet ? 1 : 0);

        const prevAvg = m.stats.averageRating || 6.5;
        const matchRating = pStat.rating || 6.5;
        const newAvg = parseFloat((((prevAvg * (m.stats.matchesPlayed || 0)) + matchRating) / newMatchesPlayed).toFixed(2));

        // Unlock achievements dynamically based on totals
        const unlocked = new Set(m.unlockedAchievements || []);
        if (newGoals >= 1) unlocked.add('first_goal');
        if (newGoals >= 10) unlocked.add('top_scorer');
        if (newAssists >= 5) unlocked.add('playmaker');
        if (newMatchesPlayed >= 10) unlocked.add('apps_10');
        if (newMatchesPlayed >= 50) unlocked.add('apps_50');
        if (newMotm >= 3) unlocked.add('motm_king');
        if (pStat.goals >= 3) unlocked.add('hat_trick_hero');

        return {
          ...m,
          stats: {
            ...m.stats,
            matchesPlayed: newMatchesPlayed,
            goals: newGoals,
            assists: newAssists,
            wins: newWins,
            losses: newLosses,
            draws: newDraws,
            motmCount: newMotm,
            yellowCards: newYellows,
            redCards: newReds,
            hatTricks: newHatTricks,
            cleanSheets: newCleanSheets,
            averageRating: newAvg,
            winPct: Math.round((newWins / newMatchesPlayed) * 100),
          },
          unlockedAchievements: Array.from(unlocked),
        };
      });

      const newLog: ActivityLog = {
        id: `act_${Date.now()}`,
        groupId: prev.group.id,
        actorName: activeMember.displayName,
        action: 'MATCH_COMPLETED',
        details: `Published post-match report: Score ${resultData.teamAScore}-${resultData.teamBScore}`,
        timestamp: new Date().toISOString(),
      };

      return {
        ...prev,
        matches: nextMatches,
        members: nextMembers,
        activityLogs: [newLog, ...prev.activityLogs],
      };
    });
  };

  const handleRegisterMember = async (newMember: GroupMemberProfile, newUser: UserAccount, password: string) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newMember, newUser, password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return payload.error || 'Unable to create account';
      setIsAuthenticated(true);
      setShowRegisterModal(false);
      setShowAuthModal(false);
      const stateResponse = await fetch('/api/state');
      if (stateResponse.ok) {
        setState(applyMatchLifecycle(await stateResponse.json()));
        hydratedRef.current = true;
      }
      return null;
    } catch {
      return 'Registration failed. Please try again.';
    }
  };

  // Next Match lookup
  const upcomingMatches = sortMatchesByKickoff(state.matches.filter(m => m.status === 'upcoming' || m.status === 'live'));
  const awaitingReportMatches = sortMatchesByKickoff(state.matches.filter(m => m.status === 'awaiting_report'), 'desc');
  const completedMatches = sortMatchesByKickoff(state.matches.filter(m => m.status === 'completed'), 'desc');
  const archivedMatches = sortMatchesByKickoff(state.matches.filter(m => m.status === 'cancelled'), 'desc');
  const nextMatch = upcomingMatches[0] || awaitingReportMatches[0] || completedMatches[0] || archivedMatches[0] || state.matches[0];
  const selectedMatch = state.matches.find(m => m.id === selectedMatchId) || nextMatch;
  const matchBoardMatches = sortMatchesByKickoff(state.matches.filter(m => ['upcoming', 'live', 'awaiting_report'].includes(m.status))).slice(0, 8);
  const homeFeaturedMatch = matchBoardMatches.find((match) => match.isFeaturedHome);
  const publicBoardMatches = matchBoardMatches.filter(m => m.visibility === 'public');
  const communityBoardMatches = matchBoardMatches.filter(m => m.visibility === 'community');
  const privateBoardMatches = matchBoardMatches.filter(m => m.visibility === 'private');
  const discoveryBoardMatches = sortMatchesByKickoff(
    state.matches.filter((m) => ['upcoming', 'live', 'awaiting_report'].includes(m.status) && m.isDiscoverable),
  );
  const discoveryQuery = matchDiscoveryQuery.trim().toLowerCase();
  const filteredDiscoveryMatches = discoveryBoardMatches.filter((match) => {
    const creator = getMatchCreator(match);
    const searchable = [
      match.title,
      match.venueName,
      match.venueAddress,
      match.createdByName,
      creator?.displayName,
      creator?.phoneNumber,
      creator?.whatsappNumber,
      match.matchType,
      match.date,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const matchesQuery = !discoveryQuery || searchable.includes(discoveryQuery);
    const matchesVisibility = matchDiscoveryVisibility === 'all' || match.visibility === matchDiscoveryVisibility;
    const matchesJoinPolicy = matchDiscoveryJoinPolicy === 'all' || match.joinPolicy === matchDiscoveryJoinPolicy;
    const matchesFormat = matchDiscoveryFormat === 'all' || match.matchType === matchDiscoveryFormat;
    const matchesPriceBand = matchDiscoveryPriceBand === 'all'
      || (matchDiscoveryPriceBand === 'budget' && match.estimatedCostPerPlayer <= 150)
      || (matchDiscoveryPriceBand === 'standard' && match.estimatedCostPerPlayer > 150 && match.estimatedCostPerPlayer <= 250)
      || (matchDiscoveryPriceBand === 'premium' && match.estimatedCostPerPlayer > 250);

    return matchesQuery && matchesVisibility && matchesJoinPolicy && matchesFormat && matchesPriceBand;
  });

  const handleUpdateRSVP = (matchId: string, status: RSVPStatus) => {
    if (activeMember.isVerified === false || activeMember.verificationStatus === 'pending') {
      alert(`⏳ Account Pending Admin Verification!\nYour account (${activeMember.displayName}, User ID: ${activeMember.userId}) is waiting for admin verification.\nAn admin must verify your profile in the Admin Panel before you can RSVP.`);
      setShowAuthModal(true);
      return;
    }

    updateState(prev => {
      const existing = prev.rsvps.find(r => r.matchId === matchId && r.memberProfileId === activeMember.id);
      let nextRsvps = [...prev.rsvps];

      if (existing) {
        nextRsvps = nextRsvps.map(r =>
          r.id === existing.id ? { ...r, status, updatedAt: new Date().toISOString() } : r
        );
      } else {
        nextRsvps.push({
          id: `rsv_${Date.now()}`,
          matchId,
          userId: state.activeUserId,
          memberProfileId: activeMember.id,
          status,
          updatedAt: new Date().toISOString(),
          history: [{ status, timestamp: new Date().toISOString() }]
        });
      }

      // Add payment record placeholder if going
      let nextPayments = [...prev.payments];
      if (status === 'going' && !nextPayments.some(p => p.matchId === matchId && p.memberProfileId === activeMember.id)) {
        const mObj = prev.matches.find(m => m.id === matchId);
        nextPayments.push({
          id: `pay_${Date.now()}`,
          matchId,
          memberProfileId: activeMember.id,
          amount: mObj?.estimatedCostPerPlayer || 150,
          status: 'unpaid',
          paymentMethod: 'bKash'
        });
      }

      return { ...prev, rsvps: nextRsvps, payments: nextPayments };
    });
  };

  const handleOpenCreateMatch = () => {
    setAdminMode('full');
    setEditingMatchId(null);
    setShowCreateMatch(true);
  };

  const handleOpenReviewRequests = () => {
    setAdminMode('requests');
    setCurrentTab('admin');
  };

  const getMatchCreator = (match: Match) => {
    if (match.createdByMemberId) {
      const byId = membersMap.get(match.createdByMemberId);
      if (byId) return byId;
    }
    if (match.createdByName) {
      return state.members.find((member) => member.displayName === match.createdByName || member.nickname === match.createdByName);
    }
    return undefined;
  };

  const getContactHref = (phone?: string) => {
    if (!phone) return null;
    return `tel:${phone.replace(/[^+\d]/g, '')}`;
  };

  const getWhatsappHref = (phone?: string, match?: Match) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (!digits) return null;
    const message = encodeURIComponent(`Hi, I saw ${match?.title || 'your match'} on GigaBalls FC. I want details about joining.`);
    return `https://wa.me/${digits}?text=${message}`;
  };

  const ensureGoingRsvp = (rsvps: AppState['rsvps'], matchId: string, memberId: string): AppState['rsvps'] => {
    const timestamp = new Date().toISOString();
    const existing = rsvps.find((entry) => entry.matchId === matchId && entry.memberProfileId === memberId);
    if (existing) {
      return rsvps.map((entry) =>
        entry.id === existing.id
          ? { ...entry, status: 'going' as RSVPStatus, updatedAt: timestamp, history: [...entry.history, { status: 'going' as RSVPStatus, timestamp }] }
          : entry,
      );
    }

    return [
      ...rsvps,
      {
        id: `rsv_${Date.now()}_${memberId}`,
        matchId,
        userId: state.activeUserId,
        memberProfileId: memberId,
        status: 'going' as RSVPStatus,
        preferredPosition: activeMember.primaryPosition,
        updatedAt: timestamp,
        history: [{ status: 'going' as RSVPStatus, timestamp }],
      },
    ];
  };

  const handleOpenJoinRequest = (match: Match) => {
    setJoinRequestMatch(match);
  };

  const handleSubmitJoinRequest = (payload: { requesterType: 'member' | 'guest'; requesterName: string; phoneNumber: string; whatsappNumber?: string; preferredPosition?: GroupMemberProfile['primaryPosition']; note?: string; }) => {
    if (!joinRequestMatch) return;

    updateState((prev) => {
      const match = prev.matches.find((entry) => entry.id === joinRequestMatch.id);
      if (!match) return prev;

      const shouldAutoApprove = payload.requesterType === 'member'
        && match.joinPolicy === 'open_join'
        && activeMember.isVerified !== false
        && activeMember.verificationStatus !== 'pending';

      const timestamp = new Date().toISOString();
      const request: MatchJoinRequest = {
        id: `join_${Date.now()}`,
        matchId: match.id,
        requesterType: payload.requesterType,
        requesterMemberId: payload.requesterType === 'member' ? activeMember.id : undefined,
        requesterName: payload.requesterName,
        phoneNumber: payload.phoneNumber,
        whatsappNumber: payload.whatsappNumber,
        preferredPosition: payload.preferredPosition,
        note: payload.note,
        status: shouldAutoApprove ? 'approved' : 'pending',
        requestedAt: timestamp,
        reviewedAt: shouldAutoApprove ? timestamp : undefined,
        reviewedByMemberId: shouldAutoApprove ? activeMember.id : undefined,
      };

      let nextRsvps = [...prev.rsvps];
      let nextPayments = [...prev.payments];
      if (shouldAutoApprove && request.requesterMemberId) {
        nextRsvps = ensureGoingRsvp(nextRsvps, match.id, request.requesterMemberId);
        if (!nextPayments.some((payment) => payment.matchId === match.id && payment.memberProfileId === request.requesterMemberId)) {
          nextPayments = [
            ...nextPayments,
            {
              id: `pay_${Date.now()}_${request.requesterMemberId}`,
              matchId: match.id,
              memberProfileId: request.requesterMemberId,
              amount: match.estimatedCostPerPlayer || 150,
              status: 'unpaid',
              paymentMethod: 'bKash',
            },
          ];
        }
      }

      const action = shouldAutoApprove ? 'Joined Match Instantly' : 'Submitted Join Request';
      const details = shouldAutoApprove
        ? `${request.requesterName} joined ${match.title} instantly via open join.`
        : `${request.requesterName} requested a spot in ${match.title}.`;

      return {
        ...prev,
        joinRequests: [request, ...(prev.joinRequests || [])],
        rsvps: nextRsvps,
        payments: nextPayments,
        activityLogs: [{
          id: `act_join_request_${Date.now()}`,
          groupId: prev.group.id,
          matchId: match.id,
          actorName: request.requesterName,
          action,
          details,
          timestamp: 'Just now',
        }, ...prev.activityLogs],
      };
    });

    alert(
      payload.requesterType === 'member' && joinRequestMatch.joinPolicy === 'open_join'
        ? '✅ You joined this match instantly. The organizer can still see your contact info from the listing.'
        : '✅ Join request sent. The organizer can now review your request and contact number.',
    );
  };

  const handleReviewJoinRequest = (requestId: string, decision: 'approved' | 'rejected') => {
    updateState((prev) => {
      const target = (prev.joinRequests || []).find((request) => request.id === requestId);
      if (!target || target.status !== 'pending') return prev;

      const match = prev.matches.find((entry) => entry.id === target.matchId);
      let nextRsvps = [...prev.rsvps];
      let nextPayments = [...prev.payments];

      if (decision === 'approved' && target.requesterMemberId && match) {
        nextRsvps = ensureGoingRsvp(nextRsvps, target.matchId, target.requesterMemberId);
        if (!nextPayments.some((payment) => payment.matchId === target.matchId && payment.memberProfileId === target.requesterMemberId)) {
          nextPayments = [
            ...nextPayments,
            {
              id: `pay_${Date.now()}_${target.requesterMemberId}`,
              matchId: target.matchId,
              memberProfileId: target.requesterMemberId,
              amount: match.estimatedCostPerPlayer || 150,
              status: 'unpaid',
              paymentMethod: 'bKash',
            },
          ];
        }
      }

      return {
        ...prev,
        joinRequests: (prev.joinRequests || []).map((request) =>
          request.id === requestId
            ? { ...request, status: decision, reviewedAt: new Date().toISOString(), reviewedByMemberId: activeMember.id }
            : request,
        ),
        rsvps: nextRsvps,
        payments: nextPayments,
        activityLogs: [{
          id: `act_join_review_${Date.now()}`,
          groupId: prev.group.id,
          matchId: target.matchId,
          actorName: activeMember.displayName,
          action: decision === 'approved' ? 'Approved Join Request' : 'Rejected Join Request',
          details: `${activeMember.displayName} ${decision} ${target.requesterName}'s request${match ? ` for ${match.title}` : ''}.`,
          timestamp: 'Just now',
        }, ...prev.activityLogs],
      };
    });
  };

  const handleToggleFeaturedMatch = (matchId: string) => {
    updateState((prev) => ({
      ...prev,
      matches: prev.matches.map((match) => ({
        ...match,
        isFeaturedHome: match.id === matchId ? !match.isFeaturedHome : false,
      })),
    }));
  };

  const handleOpenEditMatch = (matchId: string) => {
    setSelectedMatchId(null);
    setEditingMatchId(matchId);
    setShowCreateMatch(true);
  };

  const handleSaveMatch = (matchData: Partial<Match>) => {
    const isEditing = Boolean(matchData.id);
    const matchId = matchData.id || `mat_${Date.now()}`;
    const baseCreatedAt = matchData.createdAt || new Date().toISOString();
    const visibility = matchData.visibility || 'private';
    const joinPolicy = matchData.joinPolicy || (visibility === 'public' ? 'open_join' : visibility === 'community' ? 'approval_required' : 'members_only');
    const isDiscoverable = matchData.isDiscoverable ?? (visibility !== 'private');
    const isOpenToGuests = matchData.isOpenToGuests ?? (visibility !== 'private');
    const fullMatch: Match = {
      id: matchId,
      groupId: state.group.id,
      title: matchData.title || 'Turf Match',
      date: matchData.date || new Date().toISOString().split('T')[0],
      startTime: matchData.startTime || '21:00',
      endTime: matchData.endTime || '22:00',
      durationMinutes: matchData.durationMinutes || 60,
      matchType: matchData.matchType || '7v7',
      maxPlayers: matchData.maxPlayers || 14,
      minPlayers: matchData.minPlayers || 10,
      venueId: matchData.venueId || state.venues[0].id,
      venueName: matchData.venueName || state.venues[0].name,
      venueAddress: matchData.venueAddress || state.venues[0].address,
      venueMapUrl: matchData.venueMapUrl || state.venues[0].googleMapsUrl,
      totalTurfFee: matchData.totalTurfFee || 2100,
      organizerContribution: matchData.organizerContribution || 0,
      extraCost: matchData.extraCost || 0,
      gkDiscount: matchData.gkDiscount || 0,
      guestFee: matchData.guestFee || 150,
      customPlayerCost: matchData.customPlayerCost,
      estimatedCostPerPlayer: matchData.estimatedCostPerPlayer || 150,
      isCostFrozen: matchData.isCostFrozen || false,
      finalCostPerPlayer: matchData.finalCostPerPlayer,
      rsvpDeadline: matchData.rsvpDeadline || `${matchData.date}T18:00:00Z`,
      visibility,
      joinPolicy,
      isDiscoverable,
      isOpenToGuests,
      isLocked: matchData.isLocked || false,
      isCancelled: matchData.isCancelled || false,
      status: matchData.status || 'upcoming',
      rulesNote: matchData.rulesNote,
      result: matchData.result,
      motmWinnerMemberId: matchData.motmWinnerMemberId,
      teamA: matchData.teamA,
      teamB: matchData.teamB,
      createdByMemberId: matchData.createdByMemberId || activeMember.id,
      createdByName: matchData.createdByName || activeMember.displayName,
      createdAt: baseCreatedAt,
      isFeaturedHome: matchData.isFeaturedHome || false,
    };

    const newLog: ActivityLog = {
      id: `act_${Date.now()}`,
      groupId: state.group.id,
      matchId,
      actorName: activeMember.displayName,
      action: isEditing ? 'Updated Match' : 'Created Match',
      details: `${isEditing ? 'Updated' : 'Created'} match: ${fullMatch.title} at ${fullMatch.venueName}`,
      timestamp: 'Just now'
    };

    updateState(prev => ({
      ...prev,
      matches: isEditing
        ? prev.matches.map(m => (m.id === matchId ? fullMatch : m))
        : [fullMatch, ...prev.matches],
      activityLogs: [newLog, ...prev.activityLogs]
    }));

    setEditingMatchId(null);
    setShowCreateMatch(false);
  };

  const handleDeleteMatch = (matchId: string) => {
    const matchToDelete = state.matches.find(m => m.id === matchId);
    if (!matchToDelete) return;
    if (!window.confirm(`Delete match "${matchToDelete.title}"? This cannot be undone.`)) return;

    const newLog: ActivityLog = {
      id: `act_${Date.now()}`,
      groupId: state.group.id,
      matchId,
      actorName: activeMember.displayName,
      action: 'Deleted Match',
      details: `Deleted match: ${matchToDelete.title}`,
      timestamp: 'Just now'
    };

    updateState(prev => ({
      ...prev,
      matches: prev.matches.filter(m => m.id !== matchId),
      rsvps: prev.rsvps.filter(r => r.matchId !== matchId),
      payments: prev.payments.filter(p => p.matchId !== matchId),
      comments: prev.comments.filter(c => c.matchId !== matchId),
      activityLogs: [newLog, ...prev.activityLogs]
    }));

    if (selectedMatchId === matchId) setSelectedMatchId(null);
  };

  const handleUpdateTeams = (matchId: string, teamA: any, teamB: any) => {
    updateState(prev => ({
      ...prev,
      matches: prev.matches.map(m =>
        m.id === matchId ? { ...m, teamA, teamB } : m
      )
    }));
  };

  const handleMarkPayment = (matchId: string, memberProfileId: string, status: 'paid' | 'unpaid') => {
    updateState(prev => {
      const existing = prev.payments.find(p => p.matchId === matchId && p.memberProfileId === memberProfileId);
      let nextPayments = [...prev.payments];

      if (existing) {
        nextPayments = nextPayments.map(p =>
          p.id === existing.id ? { ...p, status, datePaid: status === 'paid' ? new Date().toISOString() : undefined } : p
        );
      } else {
        const mObj = prev.matches.find(m => m.id === matchId);
        nextPayments.push({
          id: `pay_${Date.now()}`,
          matchId,
          memberProfileId,
          amount: mObj?.estimatedCostPerPlayer || 150,
          status,
          paymentMethod: 'bKash',
          datePaid: status === 'paid' ? new Date().toISOString() : undefined
        });
      }

      return { ...prev, payments: nextPayments };
    });
  };

  const handleAddComment = (matchId: string, text: string) => {
    const newComment: MatchComment = {
      id: `cmt_${Date.now()}`,
      matchId,
      memberProfileId: activeMember.id,
      authorName: activeMember.displayName,
      authorAvatar: state.user.avatar,
      text,
      timestamp: 'Just now',
      reactions: {}
    };

    updateState(prev => ({ ...prev, comments: [...prev.comments, newComment] }));
  };

  const handleRecordResult = (matchId: string, teamAScore: number, teamBScore: number, motmMemberId: string) => {
    const provisionalCompletedAt = new Date().toISOString();
    const newLog: ActivityLog = {
      id: `act_${Date.now()}`,
      groupId: state.group.id,
      matchId,
      actorName: activeMember.displayName,
      action: 'Score Saved — Report Pending',
      details: `Saved preliminary score ${teamAScore}-${teamBScore}. Full player stats still need to be published.`,
      timestamp: 'Just now'
    };

    const targetMatch = state.matches.find((m) => m.id === matchId);
    if (targetMatch) {
      setPostMatchReportMatch({
        ...targetMatch,
        status: 'awaiting_report',
        motmWinnerMemberId: motmMemberId,
        result: {
          teamAScore,
          teamBScore,
          completedAt: provisionalCompletedAt,
          statsRecorded: false,
          notes: 'Preliminary score saved. Waiting for full post-match report.',
        },
      });
    }

    updateState(prev => ({
      ...prev,
      matches: prev.matches.map(m =>
        m.id === matchId
          ? {
              ...m,
              status: 'awaiting_report',
              result: {
                teamAScore,
                teamBScore,
                completedAt: provisionalCompletedAt,
                statsRecorded: false,
                notes: 'Preliminary score saved. Waiting for full post-match report.',
              },
              motmWinnerMemberId: motmMemberId
            }
          : m
      ),
      activityLogs: [newLog, ...prev.activityLogs]
    }));
  };

  const handleUpdateMemberByOrganizer = (updatedMember: GroupMemberProfile, correctionReason?: string) => {
    const newLog: ActivityLog = {
      id: `act_${Date.now()}`,
      groupId: state.group.id,
      actorName: activeMember.displayName,
      action: 'Updated Player Profile',
      details: `${activeMember.displayName} updated profile for ${updatedMember.displayName}. Reason: ${correctionReason || 'Organizer correction'}`,
      timestamp: 'Just now'
    };

    updateState(prev => ({
      ...prev,
      members: prev.members.map(m => m.id === updatedMember.id ? updatedMember : m),
      activityLogs: [newLog, ...prev.activityLogs]
    }));
  };

  const handleDeleteMemberByOrganizer = (memberId: string) => {
    const target = state.members.find(m => m.id === memberId);
    if (!target) return;

    const newLog: ActivityLog = {
      id: `act_${Date.now()}`,
      groupId: state.group.id,
      actorName: activeMember.displayName,
      action: 'Deleted Player',
      details: `${activeMember.displayName} permanently deleted player: ${target.displayName} (${target.primaryPosition})`,
      timestamp: 'Just now'
    };

    // Remove the member from local state and drop any references they hold
    // (RSVPs, payments, comments, join requests, match lineups as MOTM).
    updateState(prev => ({
      ...prev,
      members: prev.members.filter(m => m.id !== memberId),
      rsvps: prev.rsvps.filter(r => r.memberProfileId !== memberId),
      payments: prev.payments.filter(p => p.memberProfileId !== memberId),
      comments: prev.comments.filter(c => c.memberProfileId !== memberId),
      joinRequests: prev.joinRequests.filter(j => j.requesterMemberId !== memberId),
      matches: prev.matches.map(m => m.motmWinnerMemberId === memberId ? { ...m, motmWinnerMemberId: undefined } : m),
      activityLogs: [newLog, ...prev.activityLogs]
    }));

    // Clean up the auth account + sessions on the server.
    fetch(`/api/members/${memberId}`, { method: 'DELETE' })
      .catch((error) => console.warn('Failed to delete member auth account:', error));
  };

  const handleShareMatchWhatsApp = (match: Match) => {
    const goingCount = state.rsvps.filter(r => r.matchId === match.id && r.status === 'going').length;
    const text = `⚽ ${match.title.toUpperCase()}
📅 ${new Date(match.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
⏰ ${match.startTime} - ${match.endTime}
📍 ${match.venueName} (${match.venueAddress})

👥 ${goingCount}/${match.maxPlayers} Confirmed
💰 Estimated ৳${match.estimatedCostPerPlayer}/person
🌍 ${getVisibilityLabel(match)} • ${getJoinPolicyLabel(match)}

Join match & view lineup:
${state.group.inviteLink}`;

    setShareTextModal(text);
  };

  const renderHomeMatchCard = (match: Match, tone: 'public' | 'community' | 'private' = 'private') => {
    const goingList = state.rsvps.filter(r => r.matchId === match.id && r.status === 'going');
    const toneClasses = tone === 'public'
      ? 'border-cyan-500/25 bg-cyan-500/5'
      : tone === 'community'
        ? 'border-fuchsia-500/25 bg-fuchsia-500/5'
        : 'border-white/5 bg-[#171c19]';
    const creator = getMatchCreator(match);
    const avatarGroup = goingList
      .slice(0, 5)
      .map((entry) => membersMap.get(entry.memberProfileId))
      .filter((member): member is GroupMemberProfile => Boolean(member));

    return (
      <button
        key={match.id}
        type="button"
        onClick={() => setSelectedMatchId(match.id)}
        className={`w-full text-left rounded-[26px] border p-4 sm:p-5 transition hover:-translate-y-0.5 hover:border-white/15 ${toneClasses}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={creator?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
              alt={creator?.displayName || match.createdByName || 'Organizer'}
              className="w-12 h-12 rounded-2xl object-cover border border-white/10 shrink-0"
            />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold">
                {creator?.displayName || match.createdByName || 'Organizer'}
              </div>
              <h4 className="text-base sm:text-lg font-black text-white truncate">{match.title}</h4>
              <p className="text-xs text-slate-400 truncate">{match.venueName}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide bg-white/5 border border-white/10 text-slate-200">
              {getVisibilityLabel(match)}
            </span>
            <span className="text-[11px] font-bold text-emerald-300">{formatTimeTo12Hour(match.startTime)}</span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-xs text-slate-300 truncate">{match.venueAddress || match.venueName}</span>
          </div>
          <div className="text-xs font-black text-white shrink-0">৳{match.estimatedCostPerPlayer}</div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex -space-x-2">
            {avatarGroup.length > 0 ? avatarGroup.map((member, index) => (
              <img
                key={`${match.id}-${member.id || index}`}
                src={member.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                alt={member.displayName || 'Player'}
                className="w-8 h-8 rounded-full border-2 border-[#171c19] object-cover"
              />
            )) : (
              <div className="w-8 h-8 rounded-full border border-dashed border-white/10 bg-white/5" />
            )}
          </div>
          <div className="text-right">
            <div className="text-[11px] text-slate-500">Joining</div>
            <div className="text-sm font-black text-white">{goingList.length}/{match.maxPlayers}</div>
          </div>
        </div>
      </button>
    );
  };

  if (authLoading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-emerald-400 font-bold">Loading GigaBalls FC…</div>;
  if (!isAuthenticated) {
    return (
      <>
        <AuthLoginModal isOpen onLogin={handleLogin} onOpenRegisterModal={() => setShowRegisterModal(true)} />
        <RegisterPlayerModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          groupId={state.group.id}
          defaultJoinCode={state.group.joinCode}
          existingMemberCount={state.members.length}
          onRegisterMember={handleRegisterMember}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-stadium-pitch stadium-lights-glow text-slate-100 font-sans antialiased selection:bg-emerald-400 selection:text-slate-950">
      
      {/* PWA / Mobile Webview App Banner */}
      <PwaInstallBanner />

      {/* Top Header Navigation */}
      <Navbar
        user={state.user}
        group={state.group}
        members={state.members}
        activeUserId={state.activeUserId}
        onLogout={handleLogout}
        onOpenNotifications={() => setShowActivityLog(true)}
        unreadCount={isAdminUser && (pendingApprovalCount + pendingJoinRequests.length) > 0 ? (pendingApprovalCount + pendingJoinRequests.length) : state.activityLogs.length}
        pendingApprovalCount={isAdminUser ? (pendingApprovalCount + pendingJoinRequests.length) : 0}
        onNavigate={(tab) => {
          setCurrentTab(tab);
          if (tab !== 'admin') setAdminMode('full');
        }}
        currentTab={currentTab}
        onOpenRegisterPlayer={() => setShowRegisterModal(true)}
        onOpenAuthModal={handleOpenReviewRequests}
        onOpenApkModal={() => setShowApkModal(true)}
      />

      {/* Main Container Body */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 pt-3 pb-28 md:pb-12">
        
        {/* DASHBOARD TAB */}
        {currentTab === 'home' && (
          <div className="space-y-8">
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-2xl border border-white/5 bg-[#121614] p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Live board</div>
                <div className="mt-2 text-2xl font-black text-white">{matchBoardMatches.length}</div>
                <div className="text-xs text-slate-400">matches in motion / waiting / upcoming</div>
              </div>
              <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-cyan-200 font-bold">Open public</div>
                <div className="mt-2 text-2xl font-black text-white">{publicBoardMatches.length}</div>
                <div className="text-xs text-cyan-100/70">discoverable to everyone</div>
              </div>
              <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-fuchsia-200 font-bold">Community</div>
                <div className="mt-2 text-2xl font-black text-white">{communityBoardMatches.length}</div>
                <div className="text-xs text-fuchsia-100/70">network / approval-based</div>
              </div>
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-emerald-200 font-bold">Private squad</div>
                <div className="mt-2 text-2xl font-black text-white">{privateBoardMatches.length}</div>
                <div className="text-xs text-emerald-100/70">members-only fixtures</div>
              </div>
            </div>

            {homeFeaturedMatch && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-white">Pinned featured match</h3>
                    <p className="text-xs text-slate-400">This hero appears only when the owner pins a normal listing.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentTab('matches')}
                      className="min-h-[44px] px-4 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-200 rounded-xl border border-slate-700 transition"
                    >
                      Open full match board
                    </button>
                    {isOrganizer && (
                      <button
                        type="button"
                        onClick={() => handleToggleFeaturedMatch(homeFeaturedMatch.id)}
                        className="min-h-[44px] px-4 py-2 bg-white/5 hover:bg-white/10 text-xs font-bold text-slate-200 rounded-xl border border-white/10 transition"
                      >
                        Unpin
                      </button>
                    )}
                  </div>
                </div>
                <NextMatchHeroCard
                  match={homeFeaturedMatch}
                  rsvps={state.rsvps}
                  membersMap={membersMap}
                  activeMemberId={activeMember.id}
                  onUpdateRSVP={(status) => handleUpdateRSVP(homeFeaturedMatch.id, status)}
                  onViewMatch={(id) => setSelectedMatchId(id)}
                  onShareMatch={handleShareMatchWhatsApp}
                  onOpenPostMatchReport={(matchId) => {
                    const target = state.matches.find((m) => m.id === matchId);
                    if (target) setPostMatchReportMatch(target);
                  }}
                />
              </div>
            )}

            <div className="bg-[#121614] border border-white/5 rounded-[28px] p-5 sm:p-6 shadow-lg space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <h3 className="font-black text-white text-lg">Available matches</h3>
                  <p className="text-xs text-slate-400">Players can scroll this clean grid, tap any listing, then decide to join or request access from the detail view.</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full">{matchBoardMatches.length} listed</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {matchBoardMatches.length > 0 ? matchBoardMatches.map((match) => {
                  const tone = match.visibility === 'public' ? 'public' : match.visibility === 'community' ? 'community' : 'private';
                  return renderHomeMatchCard(match, tone);
                }) : (
                  <div className="md:col-span-2 xl:col-span-3 rounded-2xl border border-dashed border-white/10 bg-white/5 p-6 text-center text-sm text-slate-400">
                    No live match listings yet.
                  </div>
                )}
              </div>
            </div>

            {isOrganizer && pendingJoinRequests.length > 0 && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-4 sm:p-5 space-y-4 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-black text-base sm:text-lg text-cyan-300">Player join requests waiting</h3>
                    <p className="text-xs text-slate-300">Public/community listings can now collect real player leads with phone numbers for organizer follow-up.</p>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wide text-cyan-200 bg-cyan-500/15 border border-cyan-500/30 px-3 py-1 rounded-full">{pendingJoinRequests.length} pending</span>
                </div>

                <div className="space-y-3">
                  {pendingJoinRequests.slice(0, 4).map((request) => {
                    const requestMatch = state.matches.find((m) => m.id === request.matchId);
                    const whatsappHref = getWhatsappHref(request.whatsappNumber || request.phoneNumber, requestMatch);
                    const callHref = getContactHref(request.phoneNumber);
                    return (
                      <div key={request.id} className="bg-slate-950/70 border border-cyan-500/20 rounded-2xl p-4 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                            <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 uppercase">{request.requesterType}</span>
                            {request.preferredPosition && <span className="px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-200">{request.preferredPosition}</span>}
                            {requestMatch && <span className="text-slate-400">{requestMatch.title}</span>}
                          </div>
                          <h4 className="font-extrabold text-white">{request.requesterName}</h4>
                          <p className="text-xs text-slate-300">{request.phoneNumber}{request.whatsappNumber ? ` • WhatsApp ${request.whatsappNumber}` : ''}</p>
                          {request.note && <p className="text-xs text-slate-400">{request.note}</p>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {whatsappHref && <a href={whatsappHref} target="_blank" rel="noreferrer" className="min-h-[42px] px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-xs font-bold text-slate-200 flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
                          {callHref && <a href={callHref} className="min-h-[42px] px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-xs font-bold text-slate-200 flex items-center gap-1.5"><PhoneCall className="w-4 h-4" /> Call</a>}
                          <button type="button" onClick={() => handleReviewJoinRequest(request.id, 'approved')} className="min-h-[42px] px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition">Approve</button>
                          <button type="button" onClick={() => handleReviewJoinRequest(request.id, 'rejected')} className="min-h-[42px] px-4 py-2 bg-rose-500/90 hover:bg-rose-400 text-white font-black text-xs rounded-xl transition">Reject</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {awaitingReportMatches.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 sm:p-5 space-y-4 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-black text-base sm:text-lg text-amber-300">Post-match reports waiting</h3>
                    <p className="text-xs text-slate-300">Finished matches now move out of upcoming flow automatically and wait here until score + player stats are published.</p>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wide text-amber-200 bg-amber-500/15 border border-amber-500/30 px-3 py-1 rounded-full">{awaitingReportMatches.length} pending</span>
                </div>

                <div className="space-y-3">
                  {awaitingReportMatches.slice(0, 3).map((match) => (
                    <div key={match.id} className="bg-slate-950/70 border border-amber-500/20 rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                          <span className="px-2.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 uppercase">Awaiting report</span>
                          <span className="px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300">{getVisibilityLabel(match)}</span>
                          <span className="text-slate-400">{match.date} • {formatTimeTo12Hour(match.startTime)}</span>
                        </div>
                        <h4 className="font-extrabold text-white">{match.title}</h4>
                        <p className="text-xs text-slate-400">{match.venueName} • {state.rsvps.filter(r => r.matchId === match.id && r.status === 'going').length}/{match.maxPlayers} confirmed</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setPostMatchReportMatch(match)}
                          className="min-h-[44px] px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs rounded-xl transition"
                        >
                          Complete stats now
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedMatchId(match.id)}
                          className="min-h-[44px] px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold text-xs rounded-xl border border-slate-700 transition"
                        >
                          View details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Access Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {isOrganizer && (
                <button
                  onClick={handleOpenCreateMatch}
                  className="p-3.5 rounded-xl bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-xs flex items-center justify-between shadow-sm transition-all"
                >
                  <span className="font-bold">New Match</span>
                  <Plus className="w-5 h-5 stroke-[2.5]" />
                </button>
              )}

              <button
                onClick={() => setCurrentTab('media')}
                className="p-3.5 rounded-xl bg-gradient-to-r from-orange-500/15 to-amber-500/15 border border-orange-500/30 hover:border-orange-500/50 text-orange-400 font-bold text-xs flex items-center justify-between transition-all"
              >
                <span>Photos & Videos</span>
                <ImageIcon className="w-4 h-4 text-orange-400" />
              </button>

              <button
                onClick={() => setCurrentTab('turfs')}
                className="p-3.5 rounded-xl bg-[#121614] border border-white/5 hover:border-white/10 text-white font-semibold text-xs flex items-center justify-between hover:bg-[#171c19] transition-all"
              >
                <span>Turf Directory</span>
                <MapPin className="w-4 h-4 text-[#a8c936]" />
              </button>

              <button
                onClick={() => setCurrentTab('payments')}
                className="p-3.5 rounded-xl bg-[#121614] border border-white/5 hover:border-white/10 text-white font-semibold text-xs flex items-center justify-between hover:bg-[#171c19] transition-all"
              >
                <span>Turf Ledger</span>
                <DollarSign className="w-4 h-4 text-[#a8c936]" />
              </button>

              <button
                onClick={() => setCurrentTab('leaderboard')}
                className="p-3.5 rounded-xl bg-[#121614] border border-white/5 hover:border-white/10 text-white font-semibold text-xs flex items-center justify-between hover:bg-[#171c19] transition-all"
              >
                <span>Leaderboards</span>
                <Trophy className="w-4 h-4 text-amber-400" />
              </button>
            </div>

            {/* Group Members Roster Grid */}
            <div className="bg-[#121614] border border-white/5 rounded-2xl p-5 space-y-4 shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-base text-white">Players ({state.members.length})</h3>
                  <p className="text-xs text-slate-400 font-medium">Player ratings, positions, and overall stats</p>
                </div>
                {isOrganizer && (
                  <span className="text-[11px] font-semibold text-[#a8c936] bg-[#a8c936]/10 px-3 py-1 rounded-full border border-[#a8c936]/20">
                    Organizer Mode
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {state.members.map((mem) => (
                  <div
                    key={mem.id}
                    onClick={() => setSelectedPlayerProfileMember(mem)}
                    className="bg-[#171c19] border border-white/5 p-3.5 rounded-xl flex items-center justify-between hover:border-white/10 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5">
                      <img src={mem.avatar} alt={mem.displayName} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                      <div>
                        <span className="font-semibold text-sm text-white block">{mem.displayName}</span>
                        <span className="text-[11px] text-slate-400 font-medium">{mem.primaryPosition} • OVR {mem.footballCard?.overall ?? 0}</span>
                      </div>
                    </div>

                    {isOrganizer ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setManagingPlayerId(mem.id);
                        }}
                        className="px-3 py-1 bg-white/5 hover:bg-white/10 text-[#a8c936] text-xs font-semibold rounded-lg border border-white/10 transition"
                      >
                        Manage
                      </button>
                    ) : (
                      <span className="text-xs font-bold text-[#a8c936] font-mono">{mem.footballCard?.overall ?? 0}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* MATCHES LIST TAB */}
        {currentTab === 'matches' && (
          <div className="space-y-6 max-w-6xl mx-auto pb-20">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-white">Browse Matches, Join Requests & History</h2>
                <p className="text-xs text-slate-400">Discovery-first board for public/community fixtures, plus your live squad schedule and completed history.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMatchDiscoveryQuery('');
                    setMatchDiscoveryVisibility('all');
                    setMatchDiscoveryJoinPolicy('all');
                    setMatchDiscoveryFormat('all');
                    setMatchDiscoveryPriceBand('all');
                  }}
                  className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 font-bold text-xs rounded-xl transition"
                >
                  Reset filters
                </button>
                {isOrganizer && (
                  <button
                    onClick={handleOpenCreateMatch}
                    className="px-4 py-2.5 bg-emerald-500 text-slate-950 font-black text-xs rounded-xl shadow"
                  >
                    + Create Match
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-cyan-200 font-bold">Discovery live</div>
                <div className="mt-2 text-2xl font-black text-white">{filteredDiscoveryMatches.length}</div>
                <div className="text-xs text-cyan-100/70">matches after filters</div>
              </div>
              <div className="rounded-2xl border border-white/5 bg-[#121614] p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Open join now</div>
                <div className="mt-2 text-2xl font-black text-white">{filteredDiscoveryMatches.filter((m) => m.joinPolicy === 'open_join').length}</div>
                <div className="text-xs text-slate-400">low-friction pickup spots</div>
              </div>
              <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-fuchsia-200 font-bold">Approval-needed</div>
                <div className="mt-2 text-2xl font-black text-white">{filteredDiscoveryMatches.filter((m) => m.joinPolicy === 'approval_required').length}</div>
                <div className="text-xs text-fuchsia-100/70">organizer-reviewed entries</div>
              </div>
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="text-[11px] uppercase tracking-wide text-emerald-200 font-bold">Budget-friendly</div>
                <div className="mt-2 text-2xl font-black text-white">{filteredDiscoveryMatches.filter((m) => m.estimatedCostPerPlayer <= 150).length}</div>
                <div className="text-xs text-emerald-100/70">৳150/player or below</div>
              </div>
            </div>

            <div className="bg-[#121614] border border-white/5 rounded-3xl p-5 sm:p-6 space-y-5 shadow-lg">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <h3 className="font-black text-white text-lg">Match discovery board</h3>
                  <p className="text-xs text-slate-400">Browse by area, host, join policy, format, and cost so GigaBalls feels like a real football marketplace instead of a single-fixture screen.</p>
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full">{filteredDiscoveryMatches.length} results</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <label className="block xl:col-span-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Search area / venue / host</span>
                  <input
                    type="text"
                    value={matchDiscoveryQuery}
                    onChange={(e) => setMatchDiscoveryQuery(e.target.value)}
                    placeholder="Dhaka, Bashundhara, Friday, Saif..."
                    className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-emerald-400/50"
                  />
                </label>

                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Visibility</span>
                  <select value={matchDiscoveryVisibility} onChange={(e) => setMatchDiscoveryVisibility(e.target.value as 'all' | 'public' | 'community' | 'private')} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-emerald-400/50">
                    <option value="all">All boards</option>
                    <option value="public">Public</option>
                    <option value="community">Community</option>
                    <option value="private">Private</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Join flow</span>
                  <select value={matchDiscoveryJoinPolicy} onChange={(e) => setMatchDiscoveryJoinPolicy(e.target.value as 'all' | 'open_join' | 'approval_required' | 'members_only')} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-emerald-400/50">
                    <option value="all">All join rules</option>
                    <option value="open_join">Open join</option>
                    <option value="approval_required">Approval required</option>
                    <option value="members_only">Members only</option>
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[220px,1fr,220px] gap-4">
                <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 space-y-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Format lane</div>
                    <div className="mt-1 text-xs text-slate-400">Quick-swap the board by squad size.</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-1 gap-2">
                    {(['all', '5v5', '6v6', '7v7', '8v8', 'Custom'] as const).map((format) => {
                      const active = matchDiscoveryFormat === format;
                      return (
                        <button
                          key={format}
                          type="button"
                          onClick={() => setMatchDiscoveryFormat(format)}
                          className={`min-h-[42px] rounded-xl border px-3 py-2 text-xs font-bold transition ${active ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-300' : 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'}`}
                        >
                          {format === 'all' ? 'All formats' : format}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  {filteredDiscoveryMatches.length > 0 ? (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {filteredDiscoveryMatches.map((match) => {
                        const tone = match.visibility === 'public' ? 'public' : match.visibility === 'community' ? 'community' : 'private';
                        return renderHomeMatchCard(match, tone);
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/60 p-8 text-center">
                      <div className="text-sm font-black text-white">No matches fit these filters yet</div>
                      <p className="mt-2 text-xs text-slate-400">Try another area, remove a price filter, or create a public/community listing to seed the discovery board.</p>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 space-y-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Price lane</div>
                    <div className="mt-1 text-xs text-slate-400">Filter by what players can afford fast.</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-1 gap-2">
                    {([
                      { value: 'all', label: 'All budgets' },
                      { value: 'budget', label: 'Budget ≤ ৳150' },
                      { value: 'standard', label: 'Standard ৳151–250' },
                      { value: 'premium', label: 'Premium ৳251+' },
                    ] as const).map((band) => {
                      const active = matchDiscoveryPriceBand === band.value;
                      return (
                        <button
                          key={band.value}
                          type="button"
                          onClick={() => setMatchDiscoveryPriceBand(band.value)}
                          className={`min-h-[42px] rounded-xl border px-3 py-2 text-xs font-bold transition ${active ? 'border-cyan-400/40 bg-cyan-400/15 text-cyan-200' : 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'}`}
                        >
                          {band.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {([
                { id: 'awaiting', title: 'Awaiting post-match report', caption: 'These fixtures are over. Publish score + player stats to move them into completed history.', matches: awaitingReportMatches },
                { id: 'upcoming', title: 'Upcoming & live matches', caption: 'Scheduled fixtures still open for RSVP, lineup changes, and payments.', matches: upcomingMatches },
                { id: 'completed', title: 'Completed history', caption: 'Fully published match reports with saved scorelines and player stats.', matches: completedMatches },
                { id: 'cancelled', title: 'Cancelled / archived', caption: 'Matches that were cancelled or archived.', matches: archivedMatches },
              ] as const).map((section) => {
                if (section.matches.length === 0) return null;

                return (
                  <div key={section.id} className="space-y-3">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-wide text-white">{section.title}</h3>
                      <p className="text-xs text-slate-400">{section.caption}</p>
                    </div>

                    <div className="space-y-4">
                      {section.matches.map((m) => {
                        const going = state.rsvps.filter(r => r.matchId === m.id && r.status === 'going').length;
                        const isCompleted = m.status === 'completed';
                        const isAwaitingReport = m.status === 'awaiting_report';

                        const creator = getMatchCreator(m);
                        const contactNumber = creator?.phoneNumber || creator?.whatsappNumber;
                        const callHref = getContactHref(contactNumber);
                        const whatsappHref = getWhatsappHref(contactNumber, m);
                        const canRequestJoin = m.visibility !== 'private' || m.isOpenToGuests;

                        return (
                          <div key={m.id} className="bg-slate-900 border border-slate-800/80 p-3.5 sm:p-4 rounded-2xl flex flex-col gap-3 shadow-sm">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                                  isCompleted
                                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                    : isAwaitingReport
                                      ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                      : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                }`}>
                                  {m.status.replace('_', ' ')}
                                </span>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300">{getVisibilityLabel(m)}</span>
                                <span className="text-[11px] text-slate-400">{m.date} • {formatTimeTo12Hour(m.startTime)}</span>
                              </div>
                              <h3 className="font-extrabold text-sm sm:text-[15px] text-white leading-snug">{m.title}</h3>
                              <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-emerald-400 shrink-0" /> {m.venueName} • ৳{m.estimatedCostPerPlayer}/player
                              </p>
                              <p className="text-[11px] text-slate-500">
                                Hosted by <span className="font-bold text-slate-300">{creator?.displayName || m.createdByName || 'Organizer'}</span>{contactNumber ? <span className="text-emerald-300"> • {contactNumber}</span> : null}
                              </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 border-t border-slate-800/70 pt-2.5">
                              {isCompleted && m.result ? (
                                <div className="bg-slate-950 border border-amber-500/40 px-3 py-1 rounded-xl flex items-center gap-2 font-mono">
                                  <span className="text-[11px] font-bold text-emerald-400">{m.teamA?.name || 'Green'}</span>
                                  <span className="text-xs font-black text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-lg border border-amber-500/30">
                                    {m.result.teamAScore} - {m.result.teamBScore}
                                  </span>
                                  <span className="text-[11px] font-bold text-blue-400">{m.teamB?.name || 'Blue'}</span>
                                </div>
                              ) : isAwaitingReport ? (
                                <span className="text-[11px] font-bold text-orange-300 bg-orange-500/10 px-2.5 py-1 rounded-lg border border-orange-500/30">
                                  Score & player stats pending
                                </span>
                              ) : (
                                <span className="text-[11px] font-mono font-bold text-slate-300 bg-slate-950 px-2.5 py-1 rounded-lg border border-slate-800">
                                  {going}/{m.maxPlayers} Confirmed
                                </span>
                              )}

                              {canRequestJoin && (
                                <button type="button" onClick={() => handleOpenJoinRequest(m)} className="min-h-[40px] px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-[11px] rounded-xl transition touch-manipulation flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> {m.joinPolicy === 'open_join' ? 'Join' : 'Request'}</button>
                              )}
                              {whatsappHref && <a href={whatsappHref} target="_blank" rel="noreferrer" className="min-h-[40px] px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-100 font-bold text-[11px] rounded-xl border border-white/10 transition touch-manipulation flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>}
                              {callHref && <a href={callHref} className="min-h-[40px] px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-100 font-bold text-[11px] rounded-xl border border-white/10 transition touch-manipulation flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" /> Call</a>}

                              {isAwaitingReport && isOrganizer && (
                                <button
                                  type="button"
                                  onClick={() => setPostMatchReportMatch(m)}
                                  className="min-h-[40px] px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-[11px] rounded-xl transition touch-manipulation"
                                >
                                  Complete report
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => setSelectedMatchId(m.id)}
                                className="min-h-[44px] px-4 py-2 bg-slate-800 hover:bg-slate-700 active:scale-95 text-emerald-400 font-bold text-xs rounded-xl border border-slate-700 transition touch-manipulation flex items-center justify-center gap-1.5"
                              >
                                <span>{isCompleted ? 'View Report & Lineup' : isAwaitingReport ? 'Review Match & Finish' : 'View Pitch & Details'}</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TURFS TAB */}
        {currentTab === 'turfs' && (
          <TurfDirectoryView
            isOrganizer={isOrganizer}
            userId={activeMember.userId || activeMember.id}
            matches={state.matches}
            onAttachTurf={(matchId, turf) => updateState(prev => ({
              ...prev,
              matches: prev.matches.map(m => m.id === matchId ? {
                ...m,
                venueId: turf.id,
                venueName: turf.name,
                venueAddress: turf.address,
                venueMapUrl: turf.mapsUrl || `https://maps.google.com/?q=${encodeURIComponent(`${turf.name} ${turf.address}`)}`,
              } : m),
            }))}
          />
        )}

        {/* PAYMENTS TAB */}
        {currentTab === 'payments' && (
          <PaymentsView
            payments={state.payments}
            membersMap={membersMap}
            group={state.group}
            matches={state.matches}
            isOrganizer={isOrganizer}
            onMarkPayment={handleMarkPayment}
          />
        )}

        {/* LEADERBOARD TAB */}
        {currentTab === 'leaderboard' && (
          <LeaderboardView
            members={state.members}
            onSelectMember={(mem) => setSelectedPlayerProfileMember(mem)}
          />
        )}

        {/* GROUP / COMMUNITY TAB */}
        {currentTab === 'group' && (
          <CommunityView
            announcements={state.announcements}
            polls={state.polls}
            membersMap={membersMap}
            activeMember={activeMember}
            onVotePoll={(pollId, optionId) => {
              updateState(prev => ({
                ...prev,
                polls: prev.polls.map(p => {
                  if (p.id !== pollId) return p;
                  return {
                    ...p,
                    options: p.options.map(opt => {
                      const cleanVoters = opt.voterMemberIds.filter(id => id !== activeMember.id);
                      if (opt.id === optionId) cleanVoters.push(activeMember.id);
                      return { ...opt, voterMemberIds: cleanVoters };
                    })
                  };
                })
              }));
            }}
            onCreatePoll={(question, options) => {
              const newPoll: Poll = {
                id: `pol_${Date.now()}`,
                groupId: state.group.id,
                question,
                options: options.map((opt, i) => ({ id: `opt_${i}`, text: opt, voterMemberIds: [] })),
                createdByMemberId: activeMember.id,
                createdByName: activeMember.displayName,
                createdAt: new Date().toISOString(),
                isClosed: false
              };
              updateState(prev => ({ ...prev, polls: [newPoll, ...prev.polls] }));
            }}
            onCreateAnnouncement={(title, content) => {
              const newAnn: Announcement = {
                id: `ann_${Date.now()}`,
                groupId: state.group.id,
                title,
                content,
                authorName: activeMember.displayName,
                authorRole: activeMember.role,
                timestamp: 'Just now',
                isPinned: true
              };
              updateState(prev => ({ ...prev, announcements: [newAnn, ...prev.announcements] }));
            }}
          />
        )}

        {/* MEDIA HUB & BLOG TAB */}
        {currentTab === 'media' && (
          <MediaLibraryView
            activeMember={activeMember}
            isOrganizer={isOrganizer}
          />
        )}

        {/* WORLD FOOTBALL TAB */}
        {currentTab === 'football' && (
          <WorldFootballView
            fixtures={state.worldFixtures}
            news={state.worldNews}
            onToggleFavorite={(id) => {
              updateState(prev => ({
                ...prev,
                worldFixtures: prev.worldFixtures.map(f => f.id === id ? { ...f, isFavorite: !f.isFavorite } : f)
              }));
            }}
          />
        )}

        {/* ADMIN PANEL TAB WITH STRICT ROLE GUARD */}
        {currentTab === 'admin' && (
          isAdminUser ? (
            <AdminSettingsView
              state={state}
              onUpdateState={(newState) => updateState(() => newState)}
              onUpdateGroupSettings={(updated) => updateState(prev => ({ ...prev, group: updated }))}
              onUpdateMember={(updated) => updateState(prev => ({
                ...prev,
                members: prev.members.map(m => m.id === updated.id ? updated : m)
              }))}
              onDeleteMember={handleDeleteMemberByOrganizer}
              onResetState={() => {
                window.location.reload();
              }}
              initialTab={adminMode === 'requests' ? 'members' : 'ai'}
              requestsOnly={adminMode === 'requests'}
            />
          ) : (
            <div className="max-w-2xl mx-auto my-8 bg-slate-900 border border-red-500/30 rounded-3xl p-8 text-center space-y-4 shadow-2xl">
              <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 text-red-400 rounded-2xl flex items-center justify-center mx-auto">
                <ShieldAlert className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-black text-white">Access Restricted: Admin Panel Locked</h2>
              <p className="text-xs text-slate-300 leading-relaxed max-w-lg mx-auto">
                The Master Admin Control Panel is strictly restricted to Group Owners and Admins.
                Your current active profile (<strong className="text-emerald-400">{activeMember?.displayName}</strong>) is assigned the role <span className="uppercase font-mono text-amber-400 font-bold">"{activeMember?.role}"</span>.
              </p>
              <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-xs text-slate-400 max-w-md mx-auto text-left space-y-1.5">
                <div className="font-bold text-slate-200">🔒 Admin Panel Permission Check:</div>
                <div>• Non-admin roles (players & guests) cannot edit group settings, alter financial ledgers, or modify turf fees.</div>
                <div>• Sign in with an owner or admin account to access the control panel.</div>
              </div>
              <button
                onClick={() => setCurrentTab('home')}
                className="px-6 py-3 bg-emerald-500 text-slate-950 font-black text-xs rounded-xl shadow-lg hover:bg-emerald-400 transition"
              >
                Return to Dashboard
              </button>
            </div>
          )
        )}

        {/* PROFILE TAB */}
        {currentTab === 'profile' && (
          <ProfileView
            user={state.user}
            member={activeMember}
            notificationSettings={state.notificationSettings}
            privacySettings={state.privacySettings}
            onUpdateUser={(updated) => updateState(prev => ({
              ...prev,
              user: updated,
              members: prev.members.map(m => m.id === activeMember.id ? { ...m, avatar: updated.avatar } : m)
            }))}
            onUpdateMember={(updated) => updateState(prev => ({
              ...prev,
              members: prev.members.map(m => m.id === updated.id ? updated : m)
            }))}
            onUpdateNotifications={(updated) => updateState(prev => ({ ...prev, notificationSettings: updated }))}
            onUpdatePrivacy={(updated) => updateState(prev => ({ ...prev, privacySettings: updated }))}
            onDeleteAccount={() => alert('Account anonymized and deactivated.')}
          />
        )}

      </main>

      {/* Mobile Bottom Navigation Bar */}
      <MobileNav
        currentTab={currentTab}
        onNavigate={setCurrentTab}
        isOrganizer={isOrganizer}
        onOpenCreateMatch={handleOpenCreateMatch}
      />

      {/* Create Match Modal */}
      <CreateMatchModal
        isOpen={showCreateMatch}
        onClose={() => {
          setShowCreateMatch(false);
          setEditingMatchId(null);
        }}
        venues={state.venues}
        groupSettings={state.group}
        initialMatch={state.matches.find(m => m.id === editingMatchId) || null}
        onSaveMatch={handleSaveMatch}
      />

      <JoinMatchRequestModal
        isOpen={Boolean(joinRequestMatch)}
        onClose={() => setJoinRequestMatch(null)}
        match={joinRequestMatch}
        activeMember={activeMember}
        onSubmit={handleSubmitJoinRequest}
      />

      {/* Match Details & Pitch Modal */}
      {selectedMatchId && (
        <MatchDetailsModal
          isOpen={Boolean(selectedMatchId)}
          onClose={() => setSelectedMatchId(null)}
          match={selectedMatch}
          rsvps={state.rsvps}
          membersMap={membersMap}
          payments={state.payments}
          comments={state.comments}
          groupSettings={state.group}
          activeMember={activeMember}
          onUpdateRSVP={(matchId, status) => handleUpdateRSVP(matchId, status)}
          onUpdateTeams={handleUpdateTeams}
          onMarkPayment={handleMarkPayment}
          onAddComment={handleAddComment}
          onRecordResult={handleRecordResult}
          onShareMatch={handleShareMatchWhatsApp}
          onEditMatch={handleOpenEditMatch}
          onDeleteMatch={handleDeleteMatch}
          onToggleFeaturedMatch={handleToggleFeaturedMatch}
        />
      )}

      {/* Organizer Manage Player Modal */}
      {managingPlayerId && (
        <ManagePlayerModal
          isOpen={Boolean(managingPlayerId)}
          onClose={() => setManagingPlayerId(null)}
          member={state.members.find(m => m.id === managingPlayerId) || state.members[0]}
          onUpdateMember={handleUpdateMemberByOrganizer}
          onDeleteMember={handleDeleteMemberByOrganizer}
        />
      )}

      {/* Activity Log Drawer */}
      {showActivityLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-lg">
            <ActivityLogView logs={state.activityLogs} onClose={() => setShowActivityLog(false)} />
          </div>
        </div>
      )}

      {/* Share Text Modal */}
      {shareTextModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-4">
            <h3 className="font-extrabold text-lg text-white">Share Match to WhatsApp</h3>
            <textarea
              readOnly
              rows={8}
              value={shareTextModal}
              className="w-full bg-slate-950 border border-slate-700 text-emerald-300 font-mono text-xs rounded-2xl p-3 focus:outline-none"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShareTextModal(null)}
                className="flex-1 py-2.5 bg-slate-800 text-slate-300 font-bold rounded-xl text-xs"
              >
                Close
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareTextModal);
                  setCopiedShare(true);
                  setTimeout(() => setCopiedShare(false), 2000);
                }}
                className="flex-1 py-2.5 bg-emerald-500 text-slate-950 font-black rounded-xl text-xs flex items-center justify-center gap-1.5"
              >
                <span>{copiedShare ? 'Copied to Clipboard!' : 'Copy WhatsApp Text'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Register New Player Modal */}
      <RegisterPlayerModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        groupId={state.group.id}
        defaultJoinCode={state.group.joinCode}
        existingMemberCount={state.members.length}
        onRegisterMember={handleRegisterMember}
      />

      {/* Auth & Login Account Modal */}
      <AuthLoginModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={handleLogin}
        onOpenRegisterModal={() => setShowRegisterModal(true)}
      />

      {/* APK & Web App Download Center Modal */}
      <ApkDownloadModal
        isOpen={showApkModal}
        onClose={() => setShowApkModal(false)}
      />

      {/* Player Profile Modal */}
      {selectedPlayerProfileMember && (
        <PlayerProfileModal
          isOpen={Boolean(selectedPlayerProfileMember)}
          onClose={() => setSelectedPlayerProfileMember(null)}
          member={selectedPlayerProfileMember}
          allMembers={state.members}
          matches={state.matches}
          currentUserId={activeMember.id}
          isOrganizer={isOrganizer}
          onOpenManageRating={(memberId) => {
            setSelectedPlayerProfileMember(null);
            setManagingPlayerId(memberId);
          }}
          onOpenCompare={(memberId) => {
            setComparePlayerAId(memberId);
            setIsCompareModalOpen(true);
          }}
          onSubmitCommunityRating={handleSubmitCommunityRating}
          onApplyCommunitySuggestion={handleApplyCommunitySuggestion}
          onUpdateCosmeticTheme={handleUpdateCosmeticTheme}
        />
      )}

      {/* Player Comparison Modal */}
      <PlayerComparisonModal
        isOpen={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        members={state.members}
        initialPlayerAId={comparePlayerAId}
        initialPlayerBId={comparePlayerBId}
      />

      {/* Post Match Report Flow Modal */}
      {postMatchReportMatch && (
        <PostMatchReportModal
          isOpen={Boolean(postMatchReportMatch)}
          onClose={() => setPostMatchReportMatch(null)}
          match={postMatchReportMatch}
          members={state.members}
          onPublishReport={handlePublishPostMatchReport}
        />
      )}

    </div>
  );
}
